/**
 * DCS consolidation utilities.
 * Moves related offerings into exact results when exact results are sparse
 * for Associate's/Bachelor's degrees.
 */

export const removeSchoolDuplicates = <
  T extends { organization: { id: string } },
>(
  offerings: T[],
): T[] => {
  const seen = new Set<string>();
  return offerings.filter((o) => {
    const id = o.organization.id;
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
};

export const schoolDifference = <T extends { organization: { id: string } }>(
  offerings1: T[],
  offerings2: T[],
): T[] => {
  const orgIds = new Set(offerings2?.map((o) => o.organization.id) ?? []);
  return offerings1?.filter(
    (offering1) => !orgIds.has(offering1.organization.id),
  );
};

export const programDifference = <T extends { id: string | number }>(
  offerings1: T[],
  offerings2: T[],
): T[] => {
  const ids = new Set(offerings2?.map((o) => o.id) ?? []);
  return offerings1?.filter((offering1) => !ids.has(offering1.id));
};

export const computeDcsConsolidation = <
  T extends { organization: { id: string } },
>({
  degree,
  exactOfferings,
  relatedOfferings,
}: {
  degree?: string;
  exactOfferings?: T[];
  relatedOfferings?: T[];
}): T[] => {
  if (!exactOfferings || !relatedOfferings) {
    return [];
  }
  let limit = 0;
  if (degree === "Associate's" && exactOfferings.length < 4) {
    limit = 4;
  }
  if (degree === "Bachelor's" && exactOfferings.length < 6) {
    limit = 6;
  }
  if (limit !== 0) {
    const newUniqueMatches = schoolDifference<T>(
      removeSchoolDuplicates<T>(relatedOfferings),
      exactOfferings,
    )?.slice(0, limit - exactOfferings.length);

    return newUniqueMatches;
  }

  return [];
};
